import { supabase } from '../lib/supabase';
import { recordEmailSent } from './emailTrackingService';

const PRODUCTION_URL = 'https://leadgenpr6367.builtwithrocket.new';
const FROM_NAME = 'LeadGen Pro AI';

const getSupabaseUrl = () => import.meta.env?.VITE_SUPABASE_URL || '';
const getAnonKey = () => import.meta.env?.VITE_SUPABASE_ANON_KEY || '';

const callEdgeFunction = async (functionName, body) => {
  const supabaseUrl = getSupabaseUrl();
  if (!supabaseUrl) return { success: false, error: 'Supabase URL not configured' };
  try {
    let token = getAnonKey();
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session?.access_token) token = session?.access_token;
    } catch (e) {}
    const response = await fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': getAnonKey()
      },
      body: JSON.stringify(body)
    });
    if (!response?.ok) {
      const errorText = await response?.text()?.catch(() => 'Unknown error');
      return { success: false, error: `${response?.status}: ${errorText}` };
    }
    const data = await response?.json();
    return { success: true, data };
  } catch (err) {
    console.error(`Error calling ${functionName}:`, err);
    return { success: false, error: err?.message || 'Network error' };
  }
};

/**
 * sendConfirmationEmail
 *
 * NOTE: Supabase automatically sends its own confirmation email when a user signs up.
 * This function sends a SUPPLEMENTARY branded notification email via Resend.
 * The actual verification link comes from Supabase's own email.
 */
export const sendConfirmationEmail = async ({ to, userName, confirmationUrl, userId }) => {
  if (!to) return { success: false, error: 'Email address required' };
  const result = await callEdgeFunction('send-email', {
    to,
    subject: 'Action Required: Confirm your LeadGen Pro AI account',
    from_name: FROM_NAME,
    type: 'confirmation_notice',
    user_name: userName || 'User',
    resend_url: `${PRODUCTION_URL}/email-confirmation?pending=true&email=${encodeURIComponent(to)}`,
    data: {
      userName: userName || 'User',
      resendUrl: `${PRODUCTION_URL}/email-confirmation?pending=true&email=${encodeURIComponent(to)}`,
    }
  });
  if (result?.success && userId) {
    await recordEmailSent({
      userId,
      emailAddress: to,
      emailType: 'confirmation',
      resendEmailId: result?.data?.emailId || null,
    });
  }
  return result;
};

export const sendWelcomeEmail = async ({ to, userName }) => {
  if (!to) return { success: false, error: 'Email address required' };
  return callEdgeFunction('send-email', {
    to,
    subject: 'Welcome to LeadGen Pro AI!',
    from_name: FROM_NAME,
    type: 'welcome',
    user_name: userName || 'User',
    data: { userName: userName || 'User' }
  });
};

export const sendPasswordResetEmail = async ({ to, resetUrl, userId }) => {
  if (!to) return { success: false, error: 'Email address required' };
  const result = await callEdgeFunction('send-email', {
    to,
    subject: 'Reset your LeadGen Pro AI password',
    from_name: FROM_NAME,
    type: 'password_reset',
    reset_url: resetUrl || `${PRODUCTION_URL}/reset-password`,
    data: { resetUrl: resetUrl || `${PRODUCTION_URL}/reset-password` }
  });
  if (result?.success && userId) {
    await recordEmailSent({
      userId,
      emailAddress: to,
      emailType: 'password_reset',
      resendEmailId: result?.data?.emailId || null,
    });
  }
  return result;
};

export const sendCampaignEmail = async ({ to, subject, content, fromName, replyTo }) => {
  if (!to || !subject) return { success: false, error: 'Email and subject required' };
  return callEdgeFunction('send-email', {
    to,
    subject,
    from_name: fromName || FROM_NAME,
    type: 'campaign',
    data: { content, fromName: fromName || FROM_NAME, replyTo }
  });
};

export const emailService = {
  sendConfirmationEmail,
  sendWelcomeEmail,
  sendPasswordResetEmail,
  sendCampaignEmail
};

export default emailService;
